import React, {
  useEffect,
  useState,
} from 'react';

import './PeopleList.css';

import AvatarPreview from '../avatar/AvatarPreview';
import { defaultAvatar } from '../avatar/avatarConfig';
import { unfollowUser } from './followApi';

const apiUrl =
  process.env.REACT_APP_API_URL;

const PeopleList = ({
  onBack,
  onOpenProfile,
}) => {
  const [people, setPeople] =
    useState([]);

  const [loadingIds, setLoadingIds] =
    useState([]);

  const [isLoading, setIsLoading] =
    useState(true);

  const [errorMessage, setErrorMessage] =
    useState('');

  // ==========================================
  // INCARCARE PERSOANE
  // ==========================================

  useEffect(() => {
    const fetchPeople = async () => {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const response = await fetch(
          `${apiUrl}/user/people`,
          {
            method: 'GET',
            credentials: 'include',
          }
        );

        const data =
          await response.json();

        if (!response.ok) {
          console.error(
            'Eroare HTTP persoane:',
            response.status,
            data
          );

          throw new Error(
            data?.message ||
            'Nu s-au putut incarca persoanele.'
          );
        }

        setPeople(
          Array.isArray(data)
            ? data
            : []
        );

      } catch (error) {
        console.error(
          'Eroare la incarcarea persoanelor:',
          error
        );

        setPeople([]);

        setErrorMessage(
          error.message ||
          'Nu s-au putut incarca persoanele.'
        );

      } finally {
        setIsLoading(false);
      }
    };

    fetchPeople();
  }, []);

  // ==========================================
  // DESCHIDE PROFIL USER
  // ==========================================

  const handleOpenProfile = (
    userId
  ) => {
    if (!userId) {
      return;
    }

    if (onOpenProfile) {
      onOpenProfile(userId);
    }
  };

  // ==========================================
  // FOLLOW
  // ==========================================

  const handleFollow = async (
    userId
  ) => {
    if (
      loadingIds.includes(userId)
    ) {
      return;
    }

    setLoadingIds(
      (current) => [
        ...current,
        userId,
      ]
    );

    try {
      const response = await fetch(
        `${apiUrl}/user/follow/${userId}`,
        {
          method: 'POST',
          credentials: 'include',

          headers: {
            'Content-Type':
              'application/json',
          },
        }
      );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data?.message ||
          'Nu s-a putut urmari utilizatorul.'
        );
      }

      // Actualizare imediata in PeopleList
      setPeople(
        (current) =>
          current.map(
            (person) =>
              person.user_id === userId
                ? {
                    ...person,
                    is_following: true,
                  }
                : person
          )
      );

      // Anuntam celelalte componente
      // ca lista de urmariti s-a modificat
      window.dispatchEvent(
        new CustomEvent(
          'followUpdated',
          {
            detail: {
              action: 'follow',
              userId,
            },
          }
        )
      );

    } catch (error) {
      console.error(
        'Eroare la urmarirea utilizatorului:',
        error
      );

    } finally {
      setLoadingIds(
        (current) =>
          current.filter(
            (id) =>
              id !== userId
          )
      );
    }
  };

  // ==========================================
  // NU MAI URMARI
  // ==========================================
  // Persoana ramane in lista cu butonul intors pe URMARESTE (serverul o va
  // arata din nou printre „persoane" la urmatoarea incarcare).

  const handleUnfollow = async (
    userId
  ) => {
    if (
      loadingIds.includes(userId)
    ) {
      return;
    }

    setLoadingIds(
      (current) => [
        ...current,
        userId,
      ]
    );

    try {
      await unfollowUser(userId);

      setPeople(
        (current) =>
          current.map(
            (person) =>
              person.user_id === userId
                ? {
                    ...person,
                    is_following: false,
                  }
                : person
          )
      );

    } catch (error) {
      console.error(
        'Eroare la anularea urmaririi:',
        error
      );

    } finally {
      setLoadingIds(
        (current) =>
          current.filter(
            (id) =>
              id !== userId
          )
      );
    }
  };

  // ==========================================
  // RETURN
  // ==========================================

  return (
    <div className="people-list-page">

      {/* HEADER */}

      <div className="people-list-topbar">

        <button
          type="button"
          className="people-list-back"
          onClick={onBack}
          aria-label="Inapoi"
        >
          ‹
        </button>

        <h1>
          Persoane
        </h1>

        <div className="people-list-topbar-spacer" />

      </div>

      <div className="people-list-content">

        {/* TITLU */}

        <div className="people-list-heading">

          <h2>
            Descopera persoane
          </h2>

          <p>
            Gaseste alti utilizatori ContaLearn si urmareste-le progresul.
          </p>

        </div>

        {/* LOADING */}

        {isLoading ? (

          <div className="people-list-state">
            Se incarca persoanele...
          </div>

        ) : errorMessage ? (

          <div className="people-list-state">
            {errorMessage}
          </div>

        ) : people.length === 0 ? (

          <div className="people-list-state">
            Nu exista alte persoane momentan.
          </div>

        ) : (

          <div className="people-list-items">

            {people.map((person) => {

              const isLoadingUser =
                loadingIds.includes(
                  person.user_id
                );

              const isFollowing =
                Boolean(
                  person.is_following
                );

              const personAvatar = {
                ...defaultAvatar,
                ...(person.avatar_config || {}),
              };

              return (
                <div
                  className="people-list-item"
                  key={person.user_id}
                >

                  {/* PROFIL CLICKABIL */}

                  <button
                    type="button"
                    className="people-list-profile"
                    onClick={() =>
                      handleOpenProfile(
                        person.user_id
                      )
                    }
                    aria-label={
                      `Deschide profilul ${
                        person.display_name ||
                        'utilizatorului'
                      }`
                    }
                  >

                    {/* AVATAR */}

                    <div className="people-list-avatar-wrapper">

                      <AvatarPreview
                        avatar={personAvatar}
                        className="avatar-preview-people-list"
                      />

                    </div>

                    {/* USER */}

                    <div className="people-list-user-info">

                      <div className="people-list-name">
                        {person.display_name ||
                          'Utilizator'}
                      </div>

                      <div className="people-list-username">
                        @{person.display_name ||
                          'utilizator'}
                      </div>

                    </div>

                  </button>

                  {/* FOLLOW */}

                  <button
                    type="button"
                    className={
                      `people-list-follow ${
                        isFollowing
                          ? 'people-list-following'
                          : ''
                      }`
                    }
                    disabled={
                      isLoadingUser
                    }
                    onClick={() =>
                      isFollowing
                        ? handleUnfollow(
                            person.user_id
                          )
                        : handleFollow(
                            person.user_id
                          )
                    }
                    aria-label={
                      isFollowing
                        ? `Nu mai urmari ${
                            person.display_name ||
                            'utilizatorul'
                          }`
                        : `Urmareste ${
                            person.display_name ||
                            'utilizatorul'
                          }`
                    }
                  >

                    {isLoadingUser
                      ? 'SE INCARCA...'
                      : isFollowing
                        ? 'URMARESTI'
                        : 'URMARESTE'}

                  </button>

                </div>
              );
            })}

          </div>

        )}

      </div>

    </div>
  );
};

export default PeopleList;